import {notificationApi} from '../../services/notifications/notificationApi';
import {consumeNotificationStream} from '../../services/notifications/notificationStream';
import {ReceiptNotification} from '../../services/notifications/notificationModel';
import {defaultNotificationState, NotificationState} from './notificationState';

export interface InboxSnapshot {
  state: NotificationState;
  cursor: number | null;
}

const message = (error: unknown) =>
  typeof (error as {message?: unknown})?.message === 'string'
    ? (error as {message: string}).message : 'Unable to update notifications';

const merge = (items: ReceiptNotification[], previous: ReceiptNotification[]) => {
  const old = new Map(previous.map(item => [item.id, item]));
  const unique = new Map<number, ReceiptNotification>();
  items.forEach(item => {
    unique.set(item.id, {...item, readAt: old.get(item.id)?.readAt || item.readAt});
  });
  return Array.from(unique.values()).sort((a, b) => b.id - a.id);
};

export function createNotificationSession(options: {
  token: string;
  expiresAt: number;
  seed?: InboxSnapshot;
  isCurrent: () => boolean;
  publish: (snapshot: InboxSnapshot) => void;
  invalidate: () => void;
}) {
  const {token, expiresAt, isCurrent, publish, invalidate} = options;
  let state = {...(options.seed?.state || defaultNotificationState),
    loading: false, loadingMore: false, markingRead: false};
  let cursor = options.seed?.cursor ?? null;
  let disposed = false;
  const requests = new AbortController();
  let stream: AbortController | undefined;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  let watchdog: ReturnType<typeof setTimeout> | undefined;
  let attempts = 0;
  let connectedOnce = false;
  let queue: Promise<unknown> = Promise.resolve();
  let refreshPromise: Promise<void> | undefined;
  let refreshAgain = false;
  let morePromise: Promise<void> | undefined;
  let mutations = 0;
  const active = () => !disposed && isCurrent() && Date.now() < expiresAt;
  const check = () => {
    if (!active()) throw new Error('Notification session changed or expired');
  };
  const emit = (patch: Partial<NotificationState>) => {
    if (disposed || !isCurrent()) return;
    state = {...state, ...patch};
    publish({state, cursor});
  };
  const enqueue = (operation: () => Promise<void>) => {
    const result = queue.then(async () => {
      check();
      try {
        await operation();
        check();
      } catch (error) {
        if (active()) emit({error: message(error)});
        throw error;
      }
    });
    queue = result.catch(() => {});
    return result;
  };

  const refresh = (): Promise<void> => {
    if (!active()) return Promise.reject(new Error('Notifications require an active session'));
    if (refreshPromise) {
      refreshAgain = true;
      return refreshPromise;
    }
    emit({loading: true});
    refreshPromise = enqueue(async () => {
      refreshAgain = false;
      const previous = state.items;
      const oldest = previous[previous.length - 1]?.id;
      const latest = await notificationApi.list(token, requests.signal);
      check();
      let items = latest.items;
      let nextCursor = latest.nextCursor;
      // Rebuild the loaded window, including gaps larger than a page after reconnect.
      while (oldest !== undefined && nextCursor !== null &&
        (items.length === 0 || items[items.length - 1].id > oldest)) {
        const page = await notificationApi.list(token, requests.signal, nextCursor);
        check();
        if (page.nextCursor !== null && page.nextCursor >= nextCursor) {
          throw new Error('Invalid notification pagination cursor');
        }
        items = [...items, ...page.items];
        nextCursor = page.nextCursor;
      }
      cursor = nextCursor;
      const knownReads = new Set(previous.filter(item => item.readAt).map(item => item.id));
      const staleUnread = new Set(items.filter(item => !item.readAt && knownReads.has(item.id))
        .map(item => item.id)).size;
      emit({items: merge(items, previous), unreadCount: Math.max(0, latest.unreadCount - staleUnread),
        hasMore: cursor !== null, error: null});
    }).finally(() => {
      refreshPromise = undefined;
      // Drain demand even on failure/completion races, after already-queued user actions.
      if (refreshAgain && active()) {
        refreshAgain = false;
        refreshQuietly();
      } else {
        emit({loading: false});
      }
    });
    return refreshPromise;
  };
  const refreshQuietly = () => { void refresh().catch(() => {}); };

  const loadMore = (): Promise<void> => {
    if (!active()) return Promise.reject(new Error('Notifications require an active session'));
    if (morePromise) return morePromise;
    emit({loadingMore: true});
    morePromise = enqueue(async () => {
      if (cursor === null) return;
      const before = cursor;
      const page = await notificationApi.list(token, requests.signal, before);
      check();
      if (page.nextCursor !== null && page.nextCursor >= before) {
        throw new Error('Invalid notification pagination cursor');
      }
      cursor = page.nextCursor;
      emit({items: merge([...page.items, ...state.items], state.items),
        hasMore: cursor !== null, error: null});
      // A historical page's count may predate an event; refresh the head separately.
      refreshQuietly();
    }).finally(() => {
      morePromise = undefined;
      emit({loadingMore: false});
    });
    return morePromise;
  };

  const mutate = (operation: () => Promise<void>) => {
    if (!active()) return Promise.reject(new Error('Notifications require an active session'));
    mutations += 1;
    emit({markingRead: true});
    return enqueue(operation).then(() => {
      refreshQuietly();
    }).finally(() => {
      mutations -= 1;
      emit({markingRead: mutations > 0});
    });
  };
  const markRead = (id: number) => mutate(async () => {
    if (!Number.isSafeInteger(id) || id <= 0) throw new Error('Invalid notification ID');
    const updated = await notificationApi.markRead(token, requests.signal, id);
    check();
    if (updated.id !== id || !updated.readAt) throw new Error('Invalid read notification response');
    const wasUnread = state.items.some(item => item.id === id && !item.readAt);
    emit({items: merge(state.items.map(item => item.id === id ? updated : item), state.items),
      unreadCount: Math.max(0, state.unreadCount - Number(wasUnread)), error: null});
  });
  const markAllRead = () => {
    // Capture the visible boundary at invocation, not after earlier queued requests finish.
    const throughId = state.items[0]?.id;
    return mutate(async () => {
      if (throughId === undefined) return;
      await notificationApi.markAllRead(token, requests.signal, throughId);
      check();
      const readAt = new Date().toISOString();
      const items = state.items.map(item =>
        item.id <= throughId && !item.readAt ? {...item, readAt} : item);
      emit({items, unreadCount: items.filter(item => item.id > throughId && !item.readAt).length,
        error: null});
    });
  };

  const available = () => navigator.onLine !== false && document.visibilityState !== 'hidden';
  const disconnect = () => {
    clearTimeout(retryTimer);
    clearTimeout(watchdog);
    stream?.abort();
    stream = undefined;
  };
  const connect = () => {
    disconnect();
    if (!active() || !available()) {
      emit({connectionStatus: 'offline'});
      return;
    }
    const controller = new AbortController();
    stream = controller;
    const started = Date.now();
    emit({connectionStatus: connectedOnce ? 'reconnecting' : 'connecting'});
    connectedOnce = true;
    refreshQuietly();
    watchdog = setTimeout(() => controller.abort(), 75000);
    void consumeNotificationStream(token, controller.signal, event => {
      if (!active() || stream !== controller || controller.signal.aborted) return;
      if (event === 'ready') emit({connectionStatus: 'live'});
      if (event === 'ready' || event === 'notifications-changed') {
        invalidate();
        refreshQuietly();
      }
    }).catch(error => {
      if (active() && stream === controller && !controller.signal.aborted) {
        emit({error: message(error)});
      }
    }).finally(() => {
      if (!active() || stream !== controller) return;
      clearTimeout(watchdog);
      stream = undefined;
      if (!available()) {
        emit({connectionStatus: 'offline'});
        return;
      }
      if (Date.now() - started >= 10000) attempts = 0;
      const delay = Math.min(1000 * 2 ** Math.min(attempts++, 5), 30000);
      emit({connectionStatus: 'reconnecting'});
      retryTimer = setTimeout(connect, delay);
    });
  };
  const recover = () => {
    if (!available()) {
      disconnect();
      emit({connectionStatus: 'offline'});
    } else {
      attempts = 0;
      connect();
    }
  };
  const expireTimer = setTimeout(() => {
    disconnect();
    requests.abort();
    emit({connectionStatus: 'offline', loading: false, loadingMore: false, markingRead: false});
  }, Math.min(Math.max(0, expiresAt - Date.now()), 2147483647));

  state = {...state, refresh, loadMore, markRead, markAllRead};
  emit({});
  window.addEventListener('online', recover);
  window.addEventListener('offline', recover);
  document.addEventListener('visibilitychange', recover);
  connect();

  return {
    refresh, loadMore, markRead, markAllRead,
    dispose() {
      disposed = true;
      disconnect();
      requests.abort();
      clearTimeout(expireTimer);
      window.removeEventListener('online', recover);
      window.removeEventListener('offline', recover);
      document.removeEventListener('visibilitychange', recover);
    },
  };
}
